import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { CardType } from 'shared/constants';
import { PaymentMethod, Review } from 'shared/entities';
import { ClientStats } from 'shared/types';
import SkeletonLoader from '../../../components/common/SkeletonLoader';
import { UpdateClientProfileRequest } from '../../../happypaws-shared/src/User/models/UpdateClientProfileRequest';
import { useClientProfile } from '../../../hooks/user/useClientProfile';
import { useUpdateClientProfile } from '../../../hooks/user/useUpdateClientProfile';
import { useUpdateUserLocation } from '../../../hooks/user/useUpdateUserLocation';
import { geocodeAddress } from '../../../utils/geocode';
import ClientHeader from '../ClientHeader/ClientHeader';
import AboutMeSection from './components/AboutMeSection';
import EmergencyContactSection, {
  EmergencyContactData,
} from './components/EmergencyContactSection';
import PaymentMethodsSection from './components/PaymentMethodsSection';
import PersonalInfoSection from './components/PersonalInfoSection';
import ReviewsSection from './components/ReviewsSection';
import StatsSection from './components/StatsSection';

interface ClientProfileData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  postalCode: string;
  photoUrl?: string;
  bio: string;
  memberSince: Date;
  verified: boolean;
  stats: ClientStats;
  emergencyContact: EmergencyContactData;
  paymentMethods: PaymentMethod[];
}

const initialMockProfile: ClientProfileData = {
  id: '1',
  firstName: 'John',
  lastName: 'Doe',
  email: 'john.doe@email.com',
  phone: '+1 (555) 123-4567',
  country: 'USA',
  city: 'Miami Beach',
  postalCode: '33139',
  photoUrl: undefined,
  bio: "I'm a busy professional who loves dogs and needs reliable pet care when traveling for work. I treat my pets like family!",
  memberSince: new Date('2025-01-15'),
  verified: true,
  stats: {
    totalBookings: 18,
    rating: 4.9,
    reviewCount: 12,
    responseRate: 95,
    backgroundCheckCompleted: true,
  },
  emergencyContact: {
    name: 'Jane Doe',
    relationship: 'Family',
    phone: '+1 (555) 987-6543',
  },
  paymentMethods: [
    {
      id: '1',
      type: CardType.VISA,
      last4: '4242',
      expiryMonth: 12,
      expiryYear: 2026,
      isDefault: true,
      clientId: '1',
      createdAt: new Date(),
    },
    {
      id: '2',
      type: CardType.MASTERCARD,
      last4: '1234',
      expiryMonth: 8,
      expiryYear: 2027,
      isDefault: false,
      clientId: '1',
      createdAt: new Date(),
    },
  ],
};

const mockReviews: Review[] = [
  {
    id: 'rev_001',
    bookingId: 'book_101',
    reviewerId: 'user_201',
    reviewerName: 'Marija Petrović',
    reviewerPhotoUrl: 'https://randomuser.me/api/portraits/women/12.jpg',
    sitterId: 'sitter_301',
    sitterName: 'Ana Nikolić',
    sitterPhotoUrl: 'https://randomuser.me/api/portraits/women/44.jpg',
    rating: 5,
    text: "Ana was absolutely wonderful with our dog Max. He's usually very anxious around strangers but warmed up to her immediately. She sent regular updates and photos throughout the day. Highly recommend!",
    date: new Date('2024-11-15'),
    createdAt: new Date('2024-11-16T09:23:00'),
  },
  {
    id: 'rev_002',
    bookingId: 'book_102',
    reviewerId: 'user_202',
    reviewerName: 'Stefan Jovanović',
    reviewerPhotoUrl: null,
    sitterId: 'sitter_302',
    sitterName: 'Jelena Đorđević',
    sitterPhotoUrl: 'https://randomuser.me/api/portraits/women/68.jpg',
    rating: 4,
    text: 'Jelena took great care of our two cats while we were away for the weekend. The apartment was tidy and the cats seemed relaxed when we got back. Would book again, communication could be slightly more frequent.',
    date: new Date('2024-12-03'),
    createdAt: new Date('2024-12-04T14:05:00'),
  },
  {
    id: 'rev_003',
    bookingId: 'book_103',
    reviewerId: 'user_203',
    reviewerName: 'Milica Stojanović',
    reviewerPhotoUrl: 'https://randomuser.me/api/portraits/women/31.jpg',
    sitterId: 'sitter_303',
    sitterName: 'Nikola Vasić',
    sitterPhotoUrl: 'https://randomuser.me/api/portraits/men/55.jpg',
    rating: 5,
    text: "Nikola was super reliable and professional. Our golden retriever Luna loves him! He even took her for an extra walk without us asking. We've already booked him again for next month.",
    date: new Date('2025-01-20'),
    createdAt: new Date('2025-01-21T11:47:00'),
  },
];

const ClientProfile: React.FC = () => {
  const { data: clientProfile, isLoading: isProfileLoading } = useClientProfile();
  const { mutate: updateProfile } = useUpdateClientProfile();
  const { mutate: updateLocation } = useUpdateUserLocation();

  const [profile, setProfile] = useState<ClientProfileData>(initialMockProfile);

  useEffect(() => {
    if (!clientProfile) return;

    setProfile((prev) => ({
      ...prev,
      id: clientProfile.id,
      firstName: clientProfile.firstName,
      lastName: clientProfile.lastName,
      email: clientProfile.email,
      phone: clientProfile.phone ?? '',
      photoUrl: clientProfile.photoUrl ?? undefined,
      memberSince: new Date(clientProfile.memberSince),
      verified: clientProfile.verified,
      bio: clientProfile.bio ?? '',
      country: clientProfile.country ?? '',
      city: clientProfile.city ?? '',
      postalCode: clientProfile.postalCode ?? '',
      emergencyContact: {
        name: clientProfile.emergencyContactName ?? '',
        relationship: clientProfile.emergencyContactRelationship ?? '',
        phone: clientProfile.emergencyContactPhone ?? '',
      },
    }));
  }, [clientProfile]);

  const handlePersonalInfoSave = async (data: Partial<UpdateClientProfileRequest>) => {
    updateProfile({
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone ?? null,
      photoUrl: data.photoUrl ?? null,
      city: data.city,
      country: data.country,
      postalCode: data.postalCode,
    });

    if (data.city && data.country) {
      const coords = await geocodeAddress(data.city, data.country, data.postalCode ?? '');

      if (coords) {
        updateLocation({ lat: coords.lat, lng: coords.lng });
      }
    }
  };

  const handleBioSave = (bio: string) => {
    updateProfile({ bio });
  };

  const handleEmergencyContactSave = (emergencyContact: EmergencyContactData) => {
    updateProfile({
      emergencyContactName: emergencyContact.name,
      emergencyContactRelationship: emergencyContact.relationship,
      emergencyContactPhone: emergencyContact.phone,
    });
  };

  const handleAddCard = (
    card: Omit<PaymentMethod, 'id' | 'isDefault' | 'clientId' | 'createdAt'>
  ) => {
    const newCard: PaymentMethod = {
      ...card,
      id: Date.now().toString(),
      isDefault: profile.paymentMethods.length === 0,
      clientId: profile.id,
      createdAt: new Date(),
    };
    setProfile((prev) => ({
      ...prev,
      paymentMethods: [...prev.paymentMethods, newCard],
    }));
  };

  const handleRemoveCard = (id: string) => {
    setProfile((prev) => ({
      ...prev,
      paymentMethods: prev.paymentMethods.filter((c) => c.id !== id),
    }));
  };

  const handleSetDefault = (id: string) => {
    setProfile((prev) => ({
      ...prev,
      paymentMethods: prev.paymentMethods.map((c) => ({
        ...c,
        isDefault: c.id === id,
      })),
    }));
  };

  return (
    <Box sx={{ backgroundColor: '#FAF8F5', minHeight: '100vh' }}>
      <ClientHeader />

      <SkeletonLoader isLoading={isProfileLoading} count={1}>
        <Container maxWidth="md" sx={{ py: 4 }}>
          {/* Page Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Profile
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage your personal information
            </Typography>
          </Box>

          <PersonalInfoSection
            data={{
              firstName: profile.firstName,
              lastName: profile.lastName,
              email: profile.email,
              phone: profile.phone,
              country: profile.country,
              city: profile.city,
              postalCode: profile.postalCode,
              photoUrl: profile.photoUrl,
              memberSince: profile.memberSince,
              verified: profile.verified,
            }}
            onSave={handlePersonalInfoSave}
          />

          <AboutMeSection bio={profile.bio} onSave={handleBioSave} />

          <StatsSection stats={profile.stats} />

          <ReviewsSection reviews={mockReviews} />

          <EmergencyContactSection
            data={profile.emergencyContact}
            onSave={handleEmergencyContactSave}
          />

          <PaymentMethodsSection
            paymentMethods={profile.paymentMethods}
            onAddCard={handleAddCard}
            onRemoveCard={handleRemoveCard}
            onSetDefault={handleSetDefault}
          />
        </Container>
      </SkeletonLoader>
    </Box>
  );
};

export default ClientProfile;
